import React from "react";
import { Box, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { useDataTableStyles } from "./DataTable.styles";
import { DENSITY_STYLES } from "./DataTable.theme";
import { MelaButton } from "@/ui";

export interface TablePaginationFooterProps {
  /** Whether there are more results after the current page */
  hasMore: boolean;
  /** Callback when user clicks "Next" */
  onNext: () => void;
  /** Callback when user clicks "Previous" */
  onPrev: () => void;
  /** Whether the user can go back (page > 0) */
  canGoBack: boolean;
  /** Current page number (0-indexed, used for display) */
  page?: number;
  /** Current rows per page */
  rowsPerPage?: number;
  /** Callback when rows per page changes */
  onRowsPerPageChange?: (newValue: number) => void;
  /** Available options for rows per page */
  rowsPerPageOptions?: number[];
  /** Total number of records (used to calculate total pages) */
  totalCount?: number;
  /** Callback when user wants to jump to a specific page */
  onPageChange?: (newPage: number) => void;
  /** Whether to show rows per page selector */
  showRowsPerPage?: boolean;
  /** Density for consistent padding with table rows */
  density?: "compact" | "standard" | "comfortable";
}

/**
 * Standalone pagination footer rendered as a Box (not inside a table).
 * Designed to sit below the scrollable TableContainer so it's always visible.
 */
export const TablePaginationFooter = React.memo(TablePaginationFooterInternal);

function TablePaginationFooterInternal({
  hasMore,
  onNext,
  onPrev,
  canGoBack,
  page = 0,
  rowsPerPage = 25,
  onRowsPerPageChange,
  rowsPerPageOptions = [25, 50, 100],
  totalCount,
  onPageChange,
  showRowsPerPage = true,
  density = "standard",
}: TablePaginationFooterProps) {
  const { t } = useTranslation();
  const classes = useDataTableStyles();
  const [jumpPage, setJumpPage] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const skipBlurCommitRef = React.useRef(false);

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(Number(event.target.value));
    }
  };

  // Density styles (from centralized theme)
  const densityPadding = DENSITY_STYLES[density].padding;

  const totalPages =
    totalCount != null ? Math.ceil(totalCount / rowsPerPage) : undefined;

  const parsedJumpPage = jumpPage ? parseInt(jumpPage, 10) : NaN;

  const isValidJump =
    !isNaN(parsedJumpPage) &&
    parsedJumpPage > 0 &&
    parsedJumpPage !== page + 1 &&
    (totalPages === undefined || parsedJumpPage <= totalPages);

  const canJumpToPage = !!onPageChange && (totalPages ?? 0) > 1;

  const handleJumpPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "" || /^\d+$/.test(value)) {
      setJumpPage(value);
    }
  };

  const resetJumpState = () => {
    setIsFocused(false);
    setJumpPage("");
  };

  const handleApplyJump = () => {
    if (!isValidJump) {
      resetJumpState();
      return;
    }

    onPageChange?.(parsedJumpPage - 1);
    resetJumpState();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    setJumpPage(String(page + 1));
    e.target.select();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      resetJumpState();
      return;
    }

    const nextTarget = e.relatedTarget as HTMLElement | null;
    if (nextTarget?.dataset.paginationControl === "true") {
      resetJumpState();
      return;
    }

    handleApplyJump();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }

    if (e.key === "Escape") {
      skipBlurCommitRef.current = true;
      e.currentTarget.blur();
    }
  };

  const visiblePageValue = isFocused ? jumpPage : String(page + 1);
  const inputWidthCh = Math.max(2.4, visiblePageValue.length + 1.8);

  //TODO: double check Box instead TableFooter
  return (
    <Box
      className={classes.tableFooter}
      sx={{
        flexShrink: 0,
        padding: densityPadding,
      }}
    >
      <Box className={classes.paginationContainer}>
        {showRowsPerPage && onRowsPerPageChange && (
          <Box className={classes.rowsPerPageContainer}>
            <span className={classes.paginationLabel}>
              {t("common.rowsPerPage", "Righe per pagina")}:
            </span>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              size="small"
              className={classes.paginationSelect}
              variant="outlined"
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Box className={classes.navContainer}>
          <MelaButton
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={onPrev}
            disabled={!canGoBack}
            className={classes.navButton}
            aria-label={t("common.previousPage", "Pagina precedente")}
            data-pagination-control="true"
            startIcon={<ChevronLeftIcon fontSize="small" />}
          />

          <span className={classes.pageIndicator}>
            <span className={classes.paginationLabel}>
              {t("common.page", "Pagina")}
            </span>
            {canJumpToPage ? (
              <>
                <input
                  type="text"
                  value={visiblePageValue}
                  onChange={handleJumpPageChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className={classes.inlinePageInput}
                  inputMode="numeric"
                  aria-label={t("common.jumpToPage", "Vai alla pagina")}
                  style={{ width: `${inputWidthCh}ch` }}
                />
                {isFocused && isValidJump && (
                  <MelaButton
                    iconOnly
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleApplyJump}
                    aria-label={t("common.applyJump", "Apply")}
                    data-pagination-control="true"
                    startIcon={<CheckIcon fontSize="small" />}
                  />
                )}
              </>
            ) : (
              page + 1
            )}
            {totalPages !== undefined && <span>{`/ ${totalPages}`}</span>}
          </span>

          <MelaButton
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={onNext}
            disabled={!hasMore}
            className={classes.navButton}
            aria-label={t("common.nextPage", "Pagina successiva")}
            data-pagination-control="true"
            startIcon={<ChevronRightIcon fontSize="small" />}
          />
        </Box>
      </Box>
    </Box>
  );
}
